import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import dayjs from "dayjs";
import { catchError, filter, throwError } from 'rxjs';
import { CreateEditSeasonComponent } from 'src/app/dialogs/season-manager-dialogs/create-edit-season/create-edit-season.component';
import { EndSeasonComponent } from 'src/app/dialogs/season-manager-dialogs/end-season/end-season.component';
import { HelperService } from 'src/app/helper.service';
import { SeasonModel } from 'src/app/models/season-model';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { BaseService } from 'src/app/services/base.service';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { TenantService } from 'src/app/services/tenant.service';

@Component({
    selector: 'app-season-field-admin-seasons',
    templateUrl: './season-field-admin-seasons.component.html',
    styleUrls: ['./season-field-admin-seasons.component.css'],
    standalone: false
})
export class SeasonFieldAdminSeasonsComponent extends BaseService implements OnInit {
  fieldAdminSeasonsForm: FormGroup = new FormGroup({
    filterStartDate: new FormControl(null),
    filterEndDate: new FormControl(null),
    filterStatus: new FormControl(null),
    activeSeason: new FormControl(null)
  });

  currentPage: number = 0;
  pageSize: number = 25;
  totalElements: number = 0;

  tenantId: string = null;
  defaultValue = null;

  seasonStatuses: any[] = [
    { id: null, description: 'All' },
    { id: 1, description: 'Not Started' },
    { id: 2, description: 'Started' },
    { id: 3, description: 'Ended' }
  ];

  filterStatus: any = null;

  protected readonly dayjs = dayjs;
  dateStringFormatting = 'YYYY-MM-DD';
  placeholder: string = "-";

  searchValue: string = null;
  startDate: string = null;
  endDate: string = null;
  startDateDisplay: string = '';
  endDateDisplay: string = '';

  isNoItemsFound: boolean = false;
  isFilterBtnVisible: boolean = false;

  // Custom dropdown states
  showStatusMenu: boolean = false;
  showPageSizeMenu: boolean = false;

  displayedColumns: string[] = ['seasonName', 'dateCreated', 'startDate', 'endDate', 'totalFieldsCamps', 'status', ' '];
  dataSource = new MatTableDataSource<SeasonModel>([]);

  constructor(
    public helper: HelperService,
    private router: Router,
    public nav: NavBarComponent,
    private seasonManagerService: SeasonManagerService,
    public endSeasonDialog: MatDialog,
    public createSeasonDialog: MatDialog,
    private tenantService: TenantService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.startDateDisplay = dayjs().subtract(5, 'year').format('YYYY-MM-DD');
    this.endDateDisplay = dayjs().format('YYYY-MM-DD');
    this.startDate = dayjs().subtract(5, 'year').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    this.endDate = dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ');

    this.tenantService.currenttenantId.pipe(
      filter(id => id != null)
    ).subscribe((tenantId) => {
      this.tenantId = tenantId;
      this.populateSeasonsList();
    });
  }

  get lastPage(): number {
    return Math.max(0, Math.ceil(this.totalElements / this.pageSize) - 1);
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  setSeasonStatus(status: any) {
    this.filterStatus = status;
    this.showStatusMenu = false;
    this.populateSeasonsList();
  }

  populateSeasonsList() {
    this.nav.toggleLoading(true);
    let tenantId = Number(this.tenantId || sessionStorage.getItem("selectedTenant"));
    if (!tenantId || isNaN(tenantId)) {
      this.nav.toggleLoading(false);
      return;
    }

    this.seasonManagerService.getAllPaginatedSeasonsListByTenantId(
      tenantId,
      this.currentPage,
      this.pageSize,
      this.searchValue,
      this.startDate,
      this.endDate,
      this.filterStatus?.id !== undefined ? this.filterStatus?.id : 0
    ).pipe(
      catchError(error => {
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe((response) => {
      if (response.content.length !== 0) {
        this.isNoItemsFound = false;
        this.dataSource.data = response.content;
      } else {
        this.dataSource.data = [];
        this.isNoItemsFound = true;
      }
      this.hideShowFilterReset();
      this.totalElements = response.totalElements;
      this.nav.toggleLoading(false);
    });
  }

  // Custom pagination
  goToPage(page: number) {
    if (page < 0 || page > this.lastPage) return;
    this.currentPage = page;
    this.populateSeasonsList();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 0;
    this.showPageSizeMenu = false;
    this.populateSeasonsList();
  }

  viewSeason(id: number) {
    this.router.navigate([`season/${id}/season-admin`], { queryParams: { from: this.router.url } });
  }

  openEndSeasonDialog(id: number) {
    const dialogRef = this.endSeasonDialog.open(EndSeasonComponent, {
      data: { seasonId: id }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.option === 'end') {
        this.populateSeasonsList();
      }
    });
  }

  openCreateNewSeasonDialog() {
    const dialogRef = this.createSeasonDialog.open(CreateEditSeasonComponent, {
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'create') {
        this.router.navigate([`season/${result.seasonId}/season-admin`], { queryParams: { from: this.router.url } });
      }
    });
  }

  searchPins(searchValue: string) {
    this.searchValue = searchValue.trim();
    setTimeout(() => {
      this.populateSeasonsList();
    }, 1400);
  }

  onStartDatePicked(value: string) {
    this.startDateDisplay = value;
    this.startDate = value ? dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '';
    if (this.startDate && this.endDate) {
      this.populateSeasonsList();
    }
  }

  onEndDatePicked(value: string) {
    this.endDateDisplay = value;
    this.endDate = value ? dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '';
    if (this.startDate && this.endDate) {
      this.populateSeasonsList();
    }
  }

  hideShowFilterReset() {
    const isDateChanged = this.startDateDisplay !== dayjs().subtract(5, 'year').format('YYYY-MM-DD')
      || this.endDateDisplay !== dayjs().format('YYYY-MM-DD');
    this.isFilterBtnVisible = !!(isDateChanged || this.searchValue || this.filterStatus?.id);
  }

  resetFilter() {
    this.router.navigateByUrl("/season-manager/fields", { skipLocationChange: true }).then(() => {
      this.router.navigate(["/season-manager/seasons"]);
    });
  }
}
